// src/components/Itinerary/ItineraryHeader.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  IconButton,
  Spacer,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  useToast,
  useColorMode,
  useDisclosure,
  useClipboard,
  Tooltip,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { FaShareAlt, FaUserPlus, FaBell, FaWhatsapp, FaFacebook } from "react-icons/fa";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";
import SettingsMenu from "../common/SettingsMenu";
import InviteManagerModal from "../common/InviteManagerModal";
import TravelGroupCircles from "../common/TravelGroupCircles";
import Translate from "../../components/LanguageTranslation/Translate";
import axiosInstance from "../../helpers/axiosInstance";

const ItineraryHeader = ({
  tripId,
  handleToggleOngoingTrip,
  handleAddUser,
  isBaseScreen,
  downloadPDF,
  isLoading,
}) => {
  /* ------------------------------------------------------------------ */
  /*                         BASIC HOOKS / STATE                        */
  /* ------------------------------------------------------------------ */
  const { colorMode } = useColorMode();
  const history = useHistory();
  const toast = useToast();

  /* share-link modal */
  const { isOpen, onOpen: openModal, onClose } = useDisclosure();
  const [shareUrl, setShareUrl] = useState("");
  const { hasCopied, onCopy } = useClipboard(shareUrl);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    const fetchCompleted = async () => {
      try {
        const response = await axiosInstance.get(`/bucket-list/${tripId}`);
        if (response.data?.success && response.data?.data) {
          setIsCompleted(Boolean(response.data.data.is_completed));
        }
      } catch (e) {}
    };
    if (tripId) fetchCompleted();
  }, [tripId]);

  /* invite modal */
  const inviteModal = useDisclosure();

  const handleShare = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL; // Use the base URL from .env file
    const generatedUrl = `${baseUrl}/ai-travel-planner/manage-itinerary/${tripId}`;
    setShareUrl(generatedUrl); // Set the share URL in state
    openModal(); // Open the modal
  };

  const handleWhatsAppShare = () => {
    const encodedMessage = encodeURIComponent(
      `Check out this travel itinerary: ${shareUrl}`
    );
    try {
      window.open(`https://api.whatsapp.com/send?text=${encodedMessage}`, "_blank");
    } catch (e) {}
    toast({
      title: "Opening WhatsApp",
      description:
        "A link preview will appear automatically if OG tags are added.",
      status: "info",
      duration: 4000,
      isClosable: true,
    });
  };

  const handleFacebookShare = () => {
    const encodedUrl = encodeURIComponent(shareUrl);
    try {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        "_blank"
      );
    } catch (e) {}
    toast({
      title: "Opening Facebook",
      description: "Opening Facebook share dialog. Make sure you're logged in.",
      status: "info",
      duration: 4000,
      isClosable: true,
    });
  };

  return (
    <Box
      flex="1"
      display="flex"
      flexDirection="column"
      pb={0}
      maxH="8vh"
      borderBottom="1px"
      borderColor="blackAlpha.200"
      overflowY="hidden"
    >
      <Box
        display="flex"
        alignItems="center"
        bg={colorMode === "dark" ? "gray.700" : "white"}
        width="100%"
        height="100%"
        p={4}
        pl={{ base: 0, md: 0 }}
        // pt={0}
        // pb={0}
        boxSizing="border-box"
        overflowY="hidden"
      >
        <IconButton
          icon={<ArrowBackIcon />}
          variant="ghost"
          _hover="none"
          onClick={() => history.goBack()}
        />
        <Text
          fontSize="md"
          fontWeight="bold"
          mb={0}
          color={colorMode === "dark" ? "gray.200" : "blackAlpha.700"}
        >
          <Translate text="Manage Itinerary" />
        </Text>
        <Spacer />
        <TravelGroupCircles tripId={tripId} availableResource="itinerary" />
        <Tooltip label="Remove as Ongoing Trip">
          <Box display="flex" alignItems="center" mr={2}>
            {/* {!isBaseScreen && (
              <Text mb={0} mr={2} fontSize={{ base: "xs", md: "sm", lg: "sm" }}>
                <Translate text="Ongoing Trip" />
              </Text>
            )} */}
            <Switch
              size="sm"
              isChecked={true}
              onChange={() => handleToggleOngoingTrip()} // Trigger toggle function on change
            />
          </Box>
        </Tooltip>
        <Tooltip label={isCompleted ? "Completed Trip" : "Mark as Completed"}>
          <Box display="flex" alignItems="center" mr={2}>
            <Switch
              size="sm"
              isChecked={isCompleted}
              onChange={async () => {
                const nextCompleted = !isCompleted;
                try {
                  const response = await axiosInstance.put(
                    `/bucket-list/${tripId}/update/completed`,
                    { is_completed: nextCompleted }
                  );
                  if (response.data.success) {
                    setIsCompleted(nextCompleted);
                    // If marking completed, also remove as ongoing
                    if (nextCompleted) {
                      try {
                        await axiosInstance.put(`/bucket-list/${tripId}/update/status`, { is_ongoing: false });
                        // Propagate to parent so UI clears ongoing trip immediately
                        if (typeof handleToggleOngoingTrip === "function") {
                          await handleToggleOngoingTrip();
                        }
                      } catch (e) {}
                    }
                  }
                } catch (e) {}
              }}
            />
          </Box>
        </Tooltip>
        <Tooltip label="Download">
          <IconButton
            size="sm"
            icon={<Box as={DownloadIcon} boxSize="5" />}
            aria-label="Download"
            bg="none"
            _hover={{ bg: "none" }}
            onClick={() => {
              toast({
                title: "Download Started",
                description: "Your PDF is being prepared for download. This may take some time...",
                status: "info",
                duration: 4000,
                isClosable: true,
              });
              downloadPDF();
            }}
            isLoading={isLoading}
          />
        </Tooltip>
        <Tooltip label="Invite User">
          <IconButton
            size="sm"
            icon={<Box as={FaUserPlus} boxSize="5" />}
            aria-label="Add"
            bg="none"
            _hover={{ bg: "none" }}
            onClick={inviteModal.onOpen}
          />
        </Tooltip>
        <Tooltip label="Share">
          <IconButton
            size="sm"
            icon={<FaShareAlt />}
            onClick={handleShare}
            aria-label="Share"
            bg="none"
            _hover="none"
          />
        </Tooltip>
        <SettingsMenu />
      </Box>

      {/* ================= Share-link modal ================= */}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "xs", md: "md" }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Translate text="Share Itinerary" />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              <Translate text="Anyone with this link can request access." />
            </Text>
            <Box
              bg={colorMode === "light" ? "gray.100" : "gray.700"}
              p={3}
              borderRadius="md"
              wordBreak="break-all"
            >
              {shareUrl}
            </Box>
            <Text mt={4} fontWeight="semibold">
              <Translate text="Share on" />
            </Text>
            <Box mt={4} display="flex" alignItems="center" gap={3}>
              <IconButton
                aria-label="Share on WhatsApp"
                icon={<Box as={FaWhatsapp} boxSize="5" />}
                colorScheme="whatsapp"
                variant="solid"
                color="green"
                borderRadius="full"
                borderWidth="2px"
                borderColor={colorMode === "light" ? "blackAlpha.300" : "whiteAlpha.400"}
                size="lg"
                onClick={handleWhatsAppShare}
              />
              <IconButton
                aria-label="Share on Facebook"
                icon={<Box as={FaFacebook} boxSize="5" />}
                colorScheme="facebook"
                variant="solid"
                color="blue"
                borderRadius="full"
                borderWidth="2px"
                borderColor={colorMode === "light" ? "blackAlpha.300" : "whiteAlpha.400"}
                size="lg"
                onClick={handleFacebookShare}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onCopy}
              bg="black"
              color="white"
              _hover="none"
              _active="none"
              mr={3}
            >
              <Translate text={hasCopied ? "Copied" : "Copy Link"} />
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <Translate text="Close" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ================= Invite / Manage modal ================= */}
      <InviteManagerModal
        tripId={tripId}
        isOpen={inviteModal.isOpen}
        onClose={inviteModal.onClose}
        availableResource="itinerary"
      />
    </Box>
  );
};

export default ItineraryHeader;
